import { HttpErrorResponse } from '@angular/common/http';
import { UserReadableError } from './user-readable.error';
import { BackendValidationErrors } from '../interfaces/backend-validation-errors.interface';
import { ErrorCodeTranslator } from './error-code-translator';
import * as _ from 'lodash-es';
import { BackendErrorTranslatorService } from '../../services/localization/backend-error-translator.service';

export class UserReadableApiV2Error extends UserReadableError {
    constructor(backendErrorTranslator: BackendErrorTranslatorService, httpError: HttpErrorResponse | Error | null = null) {
        const status = httpError instanceof HttpErrorResponse ? httpError.status : 0;

        if (httpError === null || !(httpError instanceof HttpErrorResponse)) {
            super($localize`Something went wrong, please try again later`, status);
            return;
        }

        // if error is client side (ie: request to server failed due to network connectivity, rxjs throwing an error)
        if (httpError.error instanceof ErrorEvent) {
            super($localize`Could not connect to the server, please try again later`, status);
            return;
        }

        if (typeof httpError.error !== 'object') {
            super($localize`Something went wrong, please try again later`, status);
            return;
        }

        const validationErrors = UserReadableApiV2Error.getValidationErrorsFromResponse(backendErrorTranslator, httpError.error);

        const errorCode = httpError.error?._error?.error ?? httpError.error?.error?.message;
        const errorDescription = httpError.error?._error?.error_description;

        let message = $localize`Something went wrong, please try again later`;

        if (errorDescription !== null && errorDescription !== undefined) {
            message = backendErrorTranslator.localizeString(errorDescription);
        } else if (errorCode !== null && errorCode !== undefined) {
            message = ErrorCodeTranslator.translate(errorCode);
        }

        super(message, status, validationErrors);
    }

    private static getValidationErrorsFromResponse(
        backendErrorTranslator: BackendErrorTranslatorService,
        response: {
            _error: {
                validation_failures: {
                    [errorCode: string]: string[];
                };
            };
        },
    ): BackendValidationErrors | null {
        const validationFailures: {
            [errorCode: string]: string[];
        } | null = _.get(response, '_error.validation_failures', null);

        if (validationFailures === null) {
            return null;
        }

        const validationErrors: BackendValidationErrors = {};
        for (const field of Object.keys(validationFailures)) {
            validationErrors[field] = {};

            for (const backendError of validationFailures[field]) {
                validationErrors[field][backendError] = backendErrorTranslator.localizeString(backendError);
            }
        }

        return validationErrors;
    }
}
